/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from 'zod'

// -- Constants
export const INPUT_MESSAGE_REQUIRED = 'Wajib diisi'

// -- Helpers for number input validation
export const requiredNumber = (msg = INPUT_MESSAGE_REQUIRED) =>
  z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(String(v))),
    z.number({ required_error: msg }).refine((n) => !Number.isNaN(n), { message: msg }),
  )

export const optionalNumber = () =>
  z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(String(v))),
    z
      .number()
      .refine((n) => !Number.isNaN(n), { message: 'Number is invalid' })
      .optional(),
  )

// -- Helpers for Select field validation
type PickSourceType = string[] | ((src: any) => any)

const pickFromKeys = (src: any, keys: string[]) => {
  if (src == null) return undefined
  const foundKey = keys.find(
    (k) =>
      Object.prototype.hasOwnProperty.call(src, k) &&
      (src as any)[k] !== undefined &&
      (src as any)[k] !== null,
  )
  return foundKey !== undefined ? (src as any)[foundKey] : undefined
}

const getSchemaKeys = (obj: z.ZodObject<any>): string[] => {
  const anyObj = obj as any
  if (anyObj._def?.shape) {
    const shape = typeof anyObj._def.shape === 'function' ? anyObj._def.shape() : anyObj._def.shape
    return Object.keys(shape ?? {})
  }
  if (anyObj.shape) return Object.keys(anyObj.shape ?? {})
  return []
}

const normalizeString = (v: unknown) => {
  if (v == null) return undefined
  const s = typeof v === 'string' ? v : String(v)
  if (s === '') return undefined
  return s
}

const normalizeObject = (
  v: unknown,
  outputSchema: z.ZodObject<any>,
  picks?: Record<string, PickSourceType>,
) => {
  if (v == null || (typeof v === 'string' && v.trim() === '')) {
    return undefined
  }

  const src: any = v
  const out: any = {}
  const mapping: Record<string, PickSourceType> =
    picks ?? Object.fromEntries(getSchemaKeys(outputSchema).map((k) => [k, [k]]))

  Object.entries(mapping).forEach(([outKey, from]) => {
    if (typeof from === 'function') {
      out[outKey] = (from as (s: any) => any)(src)
    } else {
      out[outKey] = pickFromKeys(src, from as string[])
    }
  })

  return out
}

const normalizeArray = (
  v: unknown,
  outputSchema: z.ZodArray<any>,
  picks?: Record<string, PickSourceType>,
) => {
  const arr = Array.isArray(v) ? v : [v]
  if (!Array.isArray(arr)) return undefined

  let normalized: any[] | undefined
  const isArrOfStr = typeof arr[0] === 'string'

  if (isArrOfStr) {
    normalized = arr.map((item) => normalizeString(item)).filter((s) => s !== undefined)
    return normalized.length > 0 ? normalized : undefined
  }

  normalized = arr
    .map((item) => normalizeObject(item, outputSchema.element, picks))
    .filter((o) => o !== undefined)

  return normalized.length > 0 ? normalized : undefined
}

const validateRequired = (
  val: unknown,
  ctx: z.RefinementCtx,
  required: boolean,
  requiredMessage: string,
) => {
  if (!required) return

  if (val === undefined) {
    ctx.addIssue({ code: 'custom', message: requiredMessage, path: [] })
  }
}

export const optionFieldObject = <TShape extends z.ZodRawShape>(cfg: {
  required?: boolean
  requiredMessage?: string
  picks?: Record<string, PickSourceType>
  outputSchema: z.ZodObject<TShape>
}) => {
  const { required = true, requiredMessage = INPUT_MESSAGE_REQUIRED, picks, outputSchema } = cfg

  const base = z.preprocess((v) => normalizeObject(v, outputSchema, picks), outputSchema.optional())

  return base.superRefine((val, ctx) => {
    if (typeof required === 'boolean') {
      validateRequired(val, ctx, required, requiredMessage)
    }
  })
}

export const optionFieldString = (
  cfg: {
    required?: boolean
    message?: string
  } = {},
) => {
  const { required = true, message = 'Wajib diisi' } = cfg

  const inner = required ? z.string({ required_error: message }) : z.string().optional()

  return z.preprocess(normalizeString, inner)
}

export const optionFieldArray = <TShape extends z.ZodRawShape>(cfg: {
  required?: boolean
  requiredMessage?: string
  picks?: Record<string, PickSourceType>
  outputSchema: z.ZodArray<z.ZodString> | z.ZodArray<z.ZodObject<TShape>>
}) => {
  const { required = true, requiredMessage = INPUT_MESSAGE_REQUIRED, picks, outputSchema } = cfg

  const base = z.preprocess((v) => normalizeArray(v, outputSchema, picks), outputSchema.optional())

  return base.superRefine((val, ctx) => {
    validateRequired(val, ctx, required, requiredMessage)
  })
}
